import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  Camera, ImagePlus, ScanLine, Loader2, Sparkles, CheckCircle2,
  CloudOff, X, ChevronDown, ChevronUp, Fuel,
} from "lucide-react";
import { toast } from "sonner";
import { enqueueRefuel } from "@/lib/offlineQueue";

const RECEIPT_BANNER = "https://images.unsplash.com/photo-1695561324569-5e47c76dc0a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxnYXMlMjBzdGF0aW9uJTIwbmVvbiUyMG5pZ2h0fGVufDB8fHx8MTc4NTIyNzI3M3ww&ixlib=rb-4.1.0&q=85";

const single = (ft) => ["Petrol", "Diesel", "CNG", "EV"].includes(ft);

export default function NewRefuel() {
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [prices, setPrices] = useState({});
  const [priceCity, setPriceCity] = useState("Ahmedabad");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoFilled, setAutoFilled] = useState({});
  const [rateSource, setRateSource] = useState("auto"); // 'auto' | 'ocr' | 'manual'
  const [online, setOnline] = useState(navigator.onLine);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [form, setForm] = useState({
    vehicle_id: "",
    odometer: "",
    amount: "",
    rate: "",
    litres: "",
    station: "",
    fuel_type: "",
    refuelled_at: new Date().toISOString().slice(0, 16),
    notes: "",
  });

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === form.vehicle_id),
    [vehicles, form.vehicle_id]
  );
  const dualFuel = selectedVehicle && selectedVehicle.fuel_type === "CNG+Petrol";
  const isEV = form.fuel_type === "EV" || (selectedVehicle && selectedVehicle.fuel_type === "EV");

  // Load vehicles + prices
  useEffect(() => {
    (async () => {
      try {
        const [v, p] = await Promise.all([
          api.get("/vehicles"),
          api.get("/settings/fuel-prices"),
        ]);
        // Item 6: Refuel excludes pure-EV vehicles.
        const list = (v.data || []).filter((x) => {
          const pt = (x.powertrain || x.fuel_type || "").toLowerCase();
          return pt !== "electric" && pt !== "ev";
        });
        setVehicles(list);
        setPrices(p.data.prices || {});
        setPriceCity(p.data.city || "Ahmedabad");
        if (list[0]) {
          const first = list[0];
          const ft = single(first.fuel_type) ? first.fuel_type : "Petrol";
          setForm((f) => ({ ...f, vehicle_id: first.id, fuel_type: ft }));
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    const onOn = () => setOnline(true);
    const onOff = () => setOnline(false);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
    };
  }, []);

  // Auto-fill rate from prevailing prices whenever fuel_type or prices change
  // (unless the user manually edited it or OCR set it)
  useEffect(() => {
    if (!form.fuel_type) return;
    if (rateSource === "manual" || rateSource === "ocr") return;
    const p = prices[form.fuel_type];
    if (p && p > 0) {
      setForm((f) => ({ ...f, rate: String(p) }));
    }
  }, [form.fuel_type, prices, rateSource]);

  // Auto-compute litres from amount ÷ rate
  useEffect(() => {
    const a = parseFloat(form.amount);
    const r = parseFloat(form.rate);
    if (!isNaN(a) && !isNaN(r) && r > 0) {
      const l = (a / r).toFixed(2);
      setForm((f) => (f.litres === l ? f : { ...f, litres: l }));
    }
  }, [form.amount, form.rate]);

  const onPickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    // If the receipt is picked, offer OCR right away when online
    if (navigator.onLine) {
      // slight delay to let preview render, but not auto-scan by default
      // user still clicks the scan button
    }
  };

  const runOcr = async () => {
    if (!file) return toast.error("Add a receipt image first");
    if (!navigator.onLine) return toast.error("Scanning needs internet. Fill fields manually.");
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ocr/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const patch = {};
      const filled = {};
      if (data.amount) { patch.amount = String(data.amount); filled.amount = true; }
      if (data.rate) { patch.rate = String(data.rate); filled.rate = true; setRateSource("ocr"); }
      if (data.station) { patch.station = data.station; filled.station = true; }
      if (data.fuel_type && (single(data.fuel_type) || data.fuel_type === "Petrol" || data.fuel_type === "CNG")) {
        // Only accept OCR fuel_type if the vehicle actually supports it
        if (!selectedVehicle || selectedVehicle.fuel_type === data.fuel_type || dualFuel) {
          patch.fuel_type = data.fuel_type; filled.fuel_type = true;
        }
      }
      if (data.refuelled_at) {
        const dt = data.refuelled_at.length >= 16 ? data.refuelled_at.slice(0, 16) : data.refuelled_at;
        patch.refuelled_at = dt; filled.refuelled_at = true;
      }
      setForm((f) => ({ ...f, ...patch }));
      setAutoFilled(filled);
      toast.success("Receipt scanned · verify fields below");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScanning(false);
    }
  };

  const buildPayload = () => ({
    vehicle_id: form.vehicle_id,
    odometer: Number(form.odometer),
    litres: Number(form.litres) || 0,
    rate: Number(form.rate) || 0,
    amount: Number(form.amount),
    station: form.station || null,
    fuel_type: form.fuel_type || null,
    refuelled_at: form.refuelled_at ? new Date(form.refuelled_at).toISOString() : null,
    notes: form.notes || null,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Select a vehicle");
    if (!form.amount) return toast.error("Enter the total amount paid");
    if (!form.odometer) return toast.error("Enter the odometer reading");
    setSaving(true);
    const payload = buildPayload();
    if (!navigator.onLine) {
      enqueueRefuel(payload);
      toast.success("You're offline — refuel queued. Sync happens automatically.");
      setSaving(false);
      nav("/refuels");
      return;
    }
    try {
      const { data: created } = await api.post("/refuels", payload);
      if (file && created?.id) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/refuels/${created.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (up) {
          toast.error("Refuel saved but receipt upload failed");
        }
      }
      toast.success("Refuel logged");
      nav("/refuels");
    } catch (e2) {
      const isNetErr = !e2?.response;
      if (isNetErr) {
        enqueueRefuel(payload);
        toast.success("Network hiccup — refuel queued for sync.");
        nav("/refuels");
      } else {
        toast.error(formatApiError(e2));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden border border-[#262626]">
        <img src={RECEIPT_BANNER} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="relative p-8 sm:p-12">
          <p className="ft-overline text-[#FF5A1F] mb-2">SNAP · VERIFY · SAVE</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">New Refuel</h1>
          <p className="mt-3 text-[#A3A3A3] max-w-xl">
            Snap the receipt or pick a photo — Gemini reads the amount, rate & litres. Add the odometer, we handle the maths.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — Receipt capture */}
        <div className="ft-card p-6 lg:col-span-2">
          <p className="ft-overline mb-4">1 · RECEIPT</p>
          {!online && (
            <div className="mb-4 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-sm p-3 flex items-center gap-2" data-testid="offline-banner">
              <CloudOff size={16} />
              Offline — scanning is disabled. Fill manually, we'll sync later.
            </div>
          )}

          {!previewUrl ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                disabled={!online}
                className="w-full bg-[#FF5A1F] text-black px-4 py-6 font-bold inline-flex items-center justify-center gap-3 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60"
                data-testid="take-photo-button"
              >
                <Camera size={22} /> Take Photo
              </button>
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                className="w-full border border-[#262626] text-white px-4 py-6 font-bold inline-flex items-center justify-center gap-3 hover:border-[#FF5A1F] hover:bg-[#1A1A1A] transition-colors duration-200"
                data-testid="choose-photo-button"
              >
                <ImagePlus size={22} /> Choose from Photos
              </button>
              <p className="text-xs text-[#737373] text-center mt-2 flex items-center justify-center gap-2">
                <Sparkles size={12} className="text-[#FF5A1F]" /> Optional — you can skip and enter details manually below.
              </p>

              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0])}
                data-testid="camera-input"
              />
              <input
                ref={galleryRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0])}
                data-testid="gallery-input"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border border-[#262626] bg-[#0A0A0A] p-2">
                <img src={previewUrl} alt="Receipt preview" className="max-h-72 mx-auto" data-testid="receipt-preview" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={runOcr}
                  disabled={scanning || !online}
                  className="flex-1 bg-[#FF5A1F] text-black px-4 py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60"
                  data-testid="scan-receipt-button"
                >
                  {scanning ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
                  {scanning ? "Scanning…" : "Scan with AI"}
                </button>
                <button
                  onClick={() => {
                    setFile(null); setPreviewUrl(null); setAutoFilled({});
                    if (cameraRef.current) cameraRef.current.value = "";
                    if (galleryRef.current) galleryRef.current.value = "";
                  }}
                  className="border border-[#262626] px-4 py-3 hover:bg-[#1A1A1A] text-[#A3A3A3] hover:text-white transition-colors duration-200"
                  data-testid="clear-receipt-button"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="text-xs text-[#737373] flex items-start gap-2">
                <Sparkles size={12} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                <span>The receipt is saved to your vault. Fields marked with a spark below were auto-filled — verify before saving.</span>
              </p>
            </div>
          )}
        </div>

        {/* RIGHT — Form */}
        <form onSubmit={submit} className="ft-card p-6 lg:col-span-3 space-y-5" data-testid="refuel-form">
          <p className="ft-overline">2 · REFUEL DETAILS</p>

          {/* Vehicle picker (big) */}
          <div>
            <label className="ft-overline block mb-2">Vehicle</label>
            <select
              required
              value={form.vehicle_id}
              onChange={(e) => {
                const v = vehicles.find((x) => x.id === e.target.value);
                let ft = form.fuel_type;
                if (v) {
                  ft = single(v.fuel_type) ? v.fuel_type : "Petrol";
                }
                setRateSource("auto");
                setForm({ ...form, vehicle_id: e.target.value, fuel_type: ft });
              }}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200"
              data-testid="refuel-vehicle-select"
            >
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{`${v.number} — ${v.name} (${v.fuel_type})`}</option>
              ))}
            </select>
            {vehicles.length === 0 && (
              <p className="text-xs text-[#F59E0B] mt-2">No vehicles yet. Ask an admin to add one first.</p>
            )}
          </div>

          {/* Fuel type: only visible for dual-fuel vehicles */}
          {dualFuel && (
            <div>
              <label className="ft-overline block mb-2 flex items-center gap-2">
                <Fuel size={12} className="text-[#FF5A1F]" /> Fuel for this fill-up
              </label>
              <div className="flex gap-2" data-testid="dual-fuel-picker">
                {["Petrol", "CNG"].map((ft) => (
                  <button
                    type="button"
                    key={ft}
                    onClick={() => { setRateSource("auto"); setForm({ ...form, fuel_type: ft }); }}
                    className={`px-4 py-3 border font-bold transition-colors duration-200 flex-1 ${
                      form.fuel_type === ft
                        ? "bg-[#FF5A1F] text-black border-[#FF5A1F]"
                        : "border-[#262626] text-[#A3A3A3] hover:border-[#FF5A1F] hover:text-white"
                    }`}
                    data-testid={`dual-fuel-${ft}`}
                  >
                    {ft} · ₹{prices[ft] || "?"}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#737373] mt-2">This vehicle runs on both — pick which one you filled.</p>
            </div>
          )}

          {/* Fuel type note for single-fuel */}
          {!dualFuel && selectedVehicle && (
            <div className="text-xs text-[#A3A3A3] font-mono border border-[#262626] px-3 py-2 inline-flex items-center gap-2" data-testid="locked-fuel-type">
              <Fuel size={12} className="text-[#FF5A1F]" />
              Fuel: <span className="text-white">{form.fuel_type || selectedVehicle.fuel_type}</span> <span className="text-[#737373]">· locked to vehicle</span>
            </div>
          )}

          {/* Odometer + Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Odometer (km)"
              testid="refuel-odometer-input"
              value={form.odometer}
              onChange={(v) => setForm({ ...form, odometer: v })}
              type="number"
              step="0.01"
              required
              placeholder="e.g. 45820"
            />
            <Field
              label="Date & Time"
              testid="refuel-datetime-input"
              value={form.refuelled_at}
              onChange={(v) => setForm({ ...form, refuelled_at: v })}
              type="datetime-local"
              filled={autoFilled.refuelled_at}
              required
            />
          </div>

          {/* Total Amount */}
          <div>
            <label className="ft-overline block mb-2">
              Total Amount Paid (₹) {autoFilled.amount && <span className="text-[#FF5A1F]">•</span>}
            </label>
            <input
              type="number"
              step="0.01"
              required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="e.g. 2500"
              className={`w-full bg-[#0A0A0A] border px-4 py-4 font-mono text-white text-2xl focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200 ${autoFilled.amount ? "border-[#FF5A1F]/60" : "border-[#262626]"}`}
              data-testid="refuel-amount-input"
            />
          </div>

          {/* Rate + Litres summary (auto-filled but editable) */}
          {!isEV && (
            <div className="border border-[#262626] bg-[#0A0A0A]/60 p-4 space-y-3">
              <button
                type="button"
                onClick={() => setShowAdvanced((s) => !s)}
                className="w-full flex items-center justify-between text-left"
                data-testid="toggle-advanced"
              >
                <div>
                  <p className="ft-overline mb-1">RATE & LITRES</p>
                  <p className="text-[#A3A3A3] text-sm">
                    {rateSource === "manual" ? "Manual" : rateSource === "ocr" ? "From receipt" : `Auto · ${priceCity} rate`}
                    {" · "}
                    <span className="font-mono">₹{form.rate || "?"}/L</span>
                    {" → "}
                    <span className="font-mono text-white">{form.litres || "?"} L</span>
                  </p>
                </div>
                {showAdvanced ? <ChevronUp size={16} className="text-[#737373]" /> : <ChevronDown size={16} className="text-[#737373]" />}
              </button>
              {showAdvanced && (
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#262626]">
                  <div>
                    <label className="ft-overline block mb-2">Rate ₹/L</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.rate}
                      onChange={(e) => { setRateSource("manual"); setForm({ ...form, rate: e.target.value }); }}
                      className={`w-full bg-[#0A0A0A] border px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200 ${autoFilled.rate ? "border-[#FF5A1F]/60" : "border-[#262626]"}`}
                      data-testid="refuel-rate-input"
                    />
                    <p className="text-xs text-[#737373] mt-1">
                      Prevailing {form.fuel_type || "Petrol"} in {priceCity}: ₹{prices[form.fuel_type] || "?"}/L
                    </p>
                  </div>
                  <div>
                    <label className="ft-overline block mb-2">Litres (computed)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.litres}
                      onChange={(e) => setForm({ ...form, litres: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200"
                      data-testid="refuel-litres-input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Optional station / notes toggle */}
          <details className="border border-[#262626] p-4 open:pb-4" data-testid="optional-fields">
            <summary className="ft-overline cursor-pointer text-[#A3A3A3] hover:text-white">Optional · Station & notes</summary>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Station"
                testid="refuel-station-input"
                value={form.station}
                onChange={(v) => setForm({ ...form, station: v })}
                filled={autoFilled.station}
                placeholder="e.g. IOCL Chandkheda"
              />
              <div>
                <label className="ft-overline block mb-2">Notes</label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200"
                  data-testid="refuel-notes-input"
                />
              </div>
            </div>
          </details>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[#FF5A1F] text-black px-6 py-4 font-bold text-lg inline-flex items-center justify-center gap-2 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60"
            data-testid="refuel-submit-button"
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
            {online ? "Log refuel" : "Queue refuel (offline)"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, required, placeholder, filled, testid }) {
  return (
    <div>
      <label className="ft-overline block mb-2">
        {label} {filled && <span className="text-[#FF5A1F]">•</span>}
      </label>
      <input
        type={type}
        step={step}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-[#0A0A0A] border px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200 ${filled ? "border-[#FF5A1F]/60" : "border-[#262626]"}`}
        data-testid={testid}
      />
    </div>
  );
}
